import React, { useEffect } from 'react';
import { Card, Typography, Switch, Space, Button, Tag, message, Input, Form, Divider } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store';
import { toggleTheme } from '../store/slices/themeSlice';
import { BulbOutlined, MoonOutlined, CloudDownloadOutlined, ReloadOutlined, SaveOutlined } from '@ant-design/icons';
import { useTriggerBackupMutation, useGetBackupStatusQuery } from '../store/api/maintenanceApi';
import { useGetSettingsQuery, useUpdateSettingsMutation } from '../store/api/settingsApi';
import { useAuth } from '../hooks/useAuth';
import { PageLayout } from '../ui';

const { Text } = Typography;

const SettingsPage: React.FC = () => {
  const theme = useSelector((state: RootState) => state.theme.theme);
  const dispatch = useDispatch();
  const { user } = useAuth();
  const isAdmin = user?.role?.name === 'admin';

  const [triggerBackup, { isLoading: isBackupRunning } ] = useTriggerBackupMutation();
  const { data: statusData, refetch: refetchStatus, isFetching: isStatusFetching } = useGetBackupStatusQuery();
  const { data: settings, refetch: refetchSettings } = useGetSettingsQuery();
  const [updateSettings, { isLoading: isUpdatingSettings }] = useUpdateSettingsMutation();
  const [form] = Form.useForm();

  async function handleBackup() {
    try {
      await triggerBackup().unwrap();
      message.success('Бэкап запущен');
      setTimeout(() => refetchStatus(), 1500);
    } catch (e: any) {
      message.error(e?.data?.error || 'Не удалось запустить бэкап');
    }
  }

  const handleSaveCompanyName = async () => {
    try {
      const values = await form.validateFields();
      await updateSettings({ companyName: values.companyName }).unwrap();
      message.success('Название компании обновлено');
      refetchSettings();
    } catch (error: any) {
      message.error(error?.data?.message || 'Не удалось обновить название компании');
    }
  };

  // Заполняем форму при загрузке настроек
  useEffect(() => {
    if (settings) {
      form.setFieldsValue({ companyName: settings.companyName });
    }
  }, [settings, form]);

  return (
    <PageLayout title="Настройки" subtitle="Тема интерфейса, резервное копирование и реквизиты компании">
      <Card style={{ maxWidth: 700, margin: '0 auto', backgroundColor: 'var(--card)', borderColor: 'var(--card-border)' }}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div>
            <Text style={{ color: 'var(--text)', fontSize: 16 }}>Тема интерфейса</Text>
            <div style={{ marginTop: 8 }}>
              <Switch
                checked={theme === 'dark'}
                checkedChildren={<MoonOutlined />} 
                unCheckedChildren={<BulbOutlined />} 
                onChange={() => dispatch(toggleTheme())}
              />
              <span style={{ marginLeft: 12, color: 'var(--text-secondary)' }}>
                {theme === 'dark' ? 'Темная' : 'Светлая'}
              </span>
            </div>
          </div>

          <div>
            <Text style={{ color: 'var(--text)', fontSize: 16 }}>Резервное копирование базы</Text>
            <div style={{ marginTop: 8, display: 'flex', gap: 12, alignItems: 'center' }}>
              <Button type="primary" icon={<CloudDownloadOutlined />} loading={isBackupRunning} onClick={handleBackup}>
                Сделать бэкап сейчас
              </Button>
              <Button icon={<ReloadOutlined />} loading={isStatusFetching} onClick={() => refetchStatus()}>
                Обновить статус
              </Button>
              {statusData?.data && (
                <span style={{ color: 'var(--text-secondary)' }}>
                  Последний статус: <Tag color={statusData.data?.lastSuccess ? 'green' : 'default'}>{statusData.data?.lastMessage || '—'}</Tag>
                </span>
              )}
            </div>
          </div>

          {isAdmin && (
            <>
              <Divider />
              <div>
                <Text style={{ color: 'var(--text)', fontSize: 16, fontWeight: 500 }}>Название компании</Text>
                <Text type="secondary" style={{ display: 'block', marginTop: 4, marginBottom: 12, fontSize: 12 }}>
                  Название компании используется для автоматической подстановки перевозчика при создании маршрута с водителем компании
                </Text>
                <Form form={form} layout="vertical">
                  <Form.Item
                    name="companyName"
                    label="Название компании"
                    rules={[{ required: true, message: 'Введите название компании' }]}
                  >
                    <Input placeholder="Введите название компании" style={{ maxWidth: 400 }} />
                  </Form.Item>
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    loading={isUpdatingSettings}
                    onClick={handleSaveCompanyName}
                  >
                    Сохранить
                  </Button>
                </Form>
              </div>
            </>
          )}
        </Space>
      </Card>
    </PageLayout>
  );
};

export default SettingsPage; 